import React, { FC, HTMLAttributes, Ref, useContext, useEffect, useState } from 'react';
import { ProductCarousel } from 'features/products-carousel';
import { Sections } from 'features/page-navigation/components/section-refs-context';
import {
  getCollectionProducts,
  getCollectionTitle,
  getCollectionMainPhoto,
} from 'features/related-products/selectors';
import { RootState } from 'typesafe-actions';
import { connect } from 'react-redux';
import { pdpSectionStyle, CollectionBlock } from 'common/components/page-section';
import { getRemoteTimeout } from 'common/selectors';
import { addMediaQueryHandlers } from 'utils/add-media-query-handlers';
import { Typography, useIsomorphicLayoutEffect } from 'fronton-react';
import { cn } from 'utils';
import { MediaQueries } from 'utils/internal-specifics/media-queries';
import { Breakpoint } from 'types/utils';
import { Styles } from 'pages/product-details/styles';
import { withStoreLogic } from '../hocs/with-store-logic';
import { ContentBlocks } from './content-blocks';

export const DATA_QA_COLLECTION_GOODS = 'pdp-collection-goods';

type CollectionGoodsMappedState = {
  collectionTitle: string;
  collectionMainPhoto?: string;
} & {
  products?: string[];
};

type CollectionGoodsProps = CollectionGoodsMappedState & HTMLAttributes<HTMLElement>;

export const CollectionGoods: FC<CollectionGoodsProps & { breakpoint: Breakpoint }> = ({
  breakpoint,
  collectionTitle,
  collectionMainPhoto,
  products = [],
  className,
  id,
}) => {
  const ref = useContext(Sections.Context).get('collectionProducts') ?? undefined;
  const [isHidden, setIsHidden] = useState(false);

  useIsomorphicLayoutEffect(() => {
    if (ref?.current) {
      const productsCount = ref.current.querySelectorAll('[data-qa="product"]').length;
      if (productsCount === 0) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }
    }
  }, [ref, setIsHidden]);

  if (products.length === 0) {
    return null;
  }

  return (
    <section
      ref={ref as Ref<HTMLDivElement>}
      className={cn(isHidden ? null : className, pdpSectionStyle)}
      id={id}
      data-qa={DATA_QA_COLLECTION_GOODS}
    >
      <CollectionBlock hidden={isHidden}>
        {collectionMainPhoto ? (
          <>
            <Typography
              className={Styles.title}
              as="h2"
              variant="h2"
              data-qa="collection-goods-section-title_mf-pdp"
            >
              {collectionTitle}
            </Typography>
            <ContentBlocks.BannerImage
              breakpoint={breakpoint}
              src={collectionMainPhoto}
              alt={collectionTitle}
            />
            <ProductCarousel
              carouselId={id}
              products={products}
              className={Styles.pdpSectionInsideInfoBlockStyle}
            />
          </>
        ) : (
          <ProductCarousel
            className={Styles.pdpSectionInsideInfoBlockStyle}
            carouselId={id}
            products={products}
            title={collectionTitle}
          />
        )}
      </CollectionBlock>
    </section>
  );
};

const CollectionGoodsWithMediaEffect: FC<CollectionGoodsProps> = ({ ...props }) => {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>('large');

  useEffect(
    () =>
      addMediaQueryHandlers(
        [
          MediaQueries.LARGE_DESK_SCREEN,
          MediaQueries.MEDIUM_DESK_SCREEN,
          MediaQueries.TABLET_SCREEN,
          MediaQueries.MOBILE_SCREEN,
        ],
        ['large', 'medium', 'tablet', 'mobile'],
        'large',
        setBreakpoint,
      ),
    [],
  );

  return <CollectionGoods breakpoint={breakpoint} {...props} />;
};

const mapStateToProps = (state: RootState) => {
  return {
    collectionTitle: getCollectionTitle(state),
    collectionMainPhoto: getCollectionMainPhoto(state),
    remoteTimeout: getRemoteTimeout(state),
  };
};

export const PdpCollectionGoods = connect(mapStateToProps)(
  withStoreLogic(CollectionGoodsWithMediaEffect, getCollectionProducts),
);
